import { Injectable } from '@angular/core';
import * as THREE from 'three';
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';
import { ConfigurationJamb } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-jamb';
import { ConfigurationCutaway } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-cutaway';
import { ShapeTypeEnum } from '../../../../../enum/workbench/shape-type.enum';
import { AngleTypeEnum } from '../../../../../enum/workbench/angle-type-enum';
import { WorkbenchTextureService } from './workbench-texture.service';
import { ConfigurationPlan3D, ConfigurationEdge3D } from '../preview-ui-models';
import { MaterialTypeEnum } from '../../../../../enum/workbench/material-type-enum';
import { inferWorkbenchEdgeSide } from './workbench-edge-utils';

@Injectable({
  providedIn: 'root'
})
export class WorkbenchMeshBuilderService {

  constructor(private readonly textureService: WorkbenchTextureService) { }

  public createShapeMesh(plan: ConfigurationPlan | ConfigurationPlan3D): THREE.Group | null {
    const group = new THREE.Group();
    let shape: THREE.Shape | null = null;
    const width = plan.width;
    const length = plan.length;

    if ((!width || !length) && !plan.diameter) {
      console.warn('Invalid dimensions for plan:', plan);
      return null;
    }

    switch (plan.shape.shapeType) {
      case ShapeTypeEnum.Rectangle: {
        shape = this.createRectShape(length, width, plan.configurationCutaways);
        break;
      }
      case ShapeTypeEnum.HalfMoon: {
        shape = this.createHalfMoonShape(length, width, plan.configurationCutaways);
        break;
      }
      case ShapeTypeEnum.Mushroom: {
        let diam: any = plan.diameter;
        if (!diam && plan.shape?.['diameter']) diam = plan.shape['diameter'];
        if (!diam && (plan as ConfigurationPlan3D).shapeDiameter) diam = (plan as ConfigurationPlan3D).shapeDiameter;

        const diameter = Number(diam) || width;
        const isFlip = plan.isFlip;
        shape = this.createMushroomShape(length, width, diameter, isFlip, plan.configurationCutaways);
        break;
      }
      case ShapeTypeEnum.Angle: {
        shape = this.createAngleShape(width, length, plan.configurationCutaways);
        break;
      }
      case ShapeTypeEnum.Circle: {
        const circleDiameter = plan.diameter || width;
        shape = this.createCircleShape(circleDiameter);
        break;
      }
      default: {
        console.warn('Unknown shape type:', plan.shape.shapeType);
        return null;
      }
    }

    if (!shape) return null;

    const thickness = (plan.materialThickness?.thickness) ? plan.materialThickness.thickness : 38;

    let geometry: THREE.BufferGeometry;
    let materials: THREE.Material[];

    if (plan.shape?.shapeType === ShapeTypeEnum.Circle) {
      const radius = (plan.diameter || plan.width) / 2;
      geometry = new THREE.CylinderGeometry(radius, radius, thickness, 64);
      geometry.rotateX(Math.PI / 2);
    } else {
      // Add rectangular holes for each cutting (evier, plaque de cuisson, etc.)
      this.addCuttingHoles(shape, plan as any);
      const extrudeSettings = {
        depth: thickness,
        bevelEnabled: false,
        curveSegments: 64
      };
      geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    }

    const matTop = new THREE.MeshBasicMaterial({ color: 0xcccccc });
    const matSide = new THREE.MeshBasicMaterial({ color: 0xcccccc });

    let texture: THREE.Texture | null = null;
    const cachedTex = this.textureService.getTexture(Number(plan.id));
    if (cachedTex) {
      texture = cachedTex;
      matTop.map = texture;
      matTop.color.setHex(0xffffff);
    }

    if (plan.shape?.shapeType === ShapeTypeEnum.Circle) {
      materials = [matSide, matTop, matTop];
    } else {
      materials = [matTop, matSide];
    }

    const mesh = new THREE.Mesh(geometry, materials);

    geometry.computeBoundingBox();
    const center = new THREE.Vector3();
    if (geometry.boundingBox) geometry.boundingBox.getCenter(center);
    geometry.center();

    // SMART PLANAR UV FIX
    if (plan.shape && plan.shape.shapeType !== ShapeTypeEnum.Circle) {
      geometry.computeVertexNormals();
      const posAttribute = geometry.attributes['position'];
      const uvAttribute = geometry.attributes['uv'];
      const normalAttribute = geometry.attributes['normal'];

      geometry.computeBoundingBox();
      if (geometry.boundingBox && normalAttribute) {
        const bMinX = geometry.boundingBox.min.x;
        const bMinY = geometry.boundingBox.min.y;

        for (let i = 0; i < posAttribute.count; i++) {
          const nz = normalAttribute.getZ(i);
          if (Math.abs(nz) > 0.5) {
            const px = posAttribute.getX(i);
            const py = posAttribute.getY(i);
            uvAttribute.setXY(i, px - bMinX, py - bMinY);
          }
        }
        uvAttribute.needsUpdate = true;
      }
    }

    const edgesCentered = new THREE.EdgesGeometry(geometry);
    const lineCentered = new THREE.LineSegments(edgesCentered, new THREE.LineBasicMaterial({ color: 0x015B8E }));

    group.add(mesh);
    group.add(lineCentered);

    // Add Edge Strips (3D)
    const strips = this.createEdgeStrips(plan, width, length, center, texture);
    strips.forEach(s => group.add(s));

    if (!plan.origin) {
      plan.origin = { x: 0, y: 0 };
    }

    group.position.set(plan.origin.x + center.x, plan.origin.y + center.y, 0);
    group.userData['centerOffset'] = { x: center.x, y: center.y };

    // Edge Band Rendering Utilities (shared by configurationEdges and jamb horizontal edges)
    const EDGE_COLOR = 0x4da6ff; // Castorama blue
    const BAND_WIDTH = 20; // 20mm wide band
    const zPos = thickness / 2 + 2; // Just above the surface

    const bandMat = new THREE.MeshBasicMaterial({
      color: EDGE_COLOR, transparent: true, opacity: 0.8,
      side: THREE.DoubleSide, depthWrite: false
    });

    const L = length;
    const W = width;
    const offX = -center.x;
    const offY = -center.y;

    const addEdgeBand = (pts: THREE.Vector3[], normal: THREE.Vector3) => {
      if (pts.length >= 2) {
        const bandPts: THREE.Vector3[] = [];
        for (const element of pts) {
          bandPts.push(
            element.clone(), 
            element.clone().add(normal.clone().multiplyScalar(BAND_WIDTH)));
        }
        const positions: number[] = [];
        const indices: number[] = [];
        for (let i = 0; i < pts.length; i++) {
          positions.push(
            bandPts[i * 2].x, 
            bandPts[i * 2].y, 0, bandPts[i * 2 + 1].x, bandPts[i * 2 + 1].y, 0);
        }
        for (let i = 0; i < pts.length - 1; i++) {
          const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
          indices.push(a, c, b, b, c, d);
        }
        const bandGeom = new THREE.BufferGeometry();
        bandGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        bandGeom.setIndex(indices);
        const bandMesh = new THREE.Mesh(bandGeom, bandMat);
        bandMesh.position.z = zPos;
        bandMesh.userData = { type: 'edge-indicator' };
        bandMesh.raycast = () => {};
        group.add(bandMesh);
      }
    };

    const addEdgeArcBand = (cx: number, cy: number, radius: number, start: number, end: number) => {
      const segments = 64;
      const pts: THREE.Vector3[] = [];
      const delta = end - start;
      for (let i = 0; i <= segments; i++) {
        const theta = start + (i / segments) * delta;
        pts.push(new THREE.Vector3(cx + Math.cos(theta) * radius, cy + Math.sin(theta) * radius, 0));
      }
      const positions: number[] = [];
      const indices: number[] = [];
      for (let i = 0; i <= segments; i++) {
        const theta = start + (i / segments) * delta;
        const cos = Math.cos(theta);
        const sin = Math.sin(theta);
        positions.push(cx + cos * radius, cy + sin * radius, 0);
        positions.push(cx + cos * (radius - BAND_WIDTH), cy + sin * (radius - BAND_WIDTH), 0);
      }
      for (let i = 0; i < segments; i++) {
        const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
        indices.push(a, c, b);
        indices.push(b, c, d);
      }
      const bandGeom = new THREE.BufferGeometry();
      bandGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      bandGeom.setIndex(indices);
      const bandMesh = new THREE.Mesh(bandGeom, bandMat);
      bandMesh.position.z = zPos;
      bandMesh.userData = { type: 'edge-indicator' };
      bandMesh.raycast = () => {};
      group.add(bandMesh);
    };

    // Edges Visualization (Blue Band Indicator)
    if (!plan.materialThickness?.canHaveSideCuts && plan.configurationEdges && plan.configurationEdges.length > 0) {

      // addEdgeBand and addEdgeArcBand are defined above (shared with jambs)

      plan.configurationEdges.forEach(edge => {
        // 'side' is a transient property set in-session only; reconstruct from startPoint/endPoint on reload
        const side = inferWorkbenchEdgeSide(plan as ConfigurationPlan, edge, 5) ?? this.inferEdgeSide(edge, L, W, plan.shape.shapeType);
        switch (plan.shape.shapeType) {
          case ShapeTypeEnum.Rectangle:
            const rectSegments = this.getRectSegmentsWithCutaways(L, W, plan.configurationCutaways);
            rectSegments.forEach(seg => {
              if (seg.side === side) {
                if (seg.isArc && seg.radius) {
                  addEdgeArcBand(seg.p1.x + offX, seg.p1.y + offY, seg.radius, seg.startAngle || 0, seg.endAngle || 0);
                } else {
                  const vec = new THREE.Vector3(seg.p2.x - seg.p1.x, seg.p2.y - seg.p1.y, 0).normalize();
                  const normal = new THREE.Vector3(-vec.y, vec.x, 0);
                  addEdgeBand([
                    new THREE.Vector3(seg.p1.x + offX, seg.p1.y + offY, 0),
                    new THREE.Vector3(seg.p2.x + offX, seg.p2.y + offY, 0)
                  ], normal);
                }
              }
            });
            break;
          case ShapeTypeEnum.HalfMoon:
            const rHM = W / 2;
            if (side === 'left') addEdgeBand(
              [new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(0 + offX, W + offY, 0)],
              new THREE.Vector3(1, 0, 0)
            );
            else if (side === 'top') addEdgeBand(
              [new THREE.Vector3(0 + offX, W + offY, 0), new THREE.Vector3(L - rHM + offX, W + offY, 0)],
              new THREE.Vector3(0, -1, 0)
            );
            else if (side === 'bottom') addEdgeBand(
              [new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(L - rHM + offX, 0 + offY, 0)],
              new THREE.Vector3(0, 1, 0)
            );
            else if (side === 'arc' || side === 'right') {
              addEdgeArcBand(L - rHM + offX, rHM + offY, rHM, -Math.PI / 2, Math.PI / 2);
            }
            break;
          case ShapeTypeEnum.Mushroom:
            const rM = (plan.diameter || W) / 2;
            const isFlipM = plan.isFlip;
            const adjXM = Math.sqrt(Math.max(0, rM ** 2 - (W - rM) ** 2));
            const stopXM = L - rM - adjXM;
            if (side === 'left') addEdgeBand(
              [new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(0 + offX, W + offY, 0)],
              new THREE.Vector3(1, 0, 0)
            );
            else if (side === 'top') {
              const xEnd = isFlipM ? (L - rM) : stopXM;
              addEdgeBand(
                [new THREE.Vector3(0 + offX, W + offY, 0), new THREE.Vector3(xEnd + offX, W + offY, 0)],
                new THREE.Vector3(0, -1, 0)
              );
            }
            else if (side === 'bottom') {
              const xEnd = isFlipM ? stopXM : (L - rM);
              addEdgeBand(
                [new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(xEnd + offX, 0 + offY, 0)],
                new THREE.Vector3(0, 1, 0)
              );
            }
            else if (side === 'arc' || side === 'right') {
              const start = isFlipM ? Math.PI / 2 : -Math.PI / 2;
              const arg = Math.max(-1, Math.min(1, (W - rM) / rM));
              const thetaPrincipal = Math.asin(arg);
              const thetaEnd = isFlipM ? (-Math.PI - thetaPrincipal) : (Math.PI - thetaPrincipal);
              const cy = isFlipM ? (W - rM) : rM;
              addEdgeArcBand(L - rM + offX, cy + offY, rM, start, thetaEnd);
            }
            break;
          case ShapeTypeEnum.Circle:
            const rC = (plan.diameter || 940) / 2;
            addEdgeArcBand(0, 0, rC, 0, Math.PI * 2);
            break;
          case ShapeTypeEnum.Angle:
            if (side === 'bottom') addEdgeBand(
              [new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(W + offX, 0 + offY, 0)],
              new THREE.Vector3(0, 1, 0)
            );
            else if (side === 'diagonal') {
              const dir = new THREE.Vector3(L - W, L - W, 0).normalize();
              const inward = new THREE.Vector3(-dir.y, dir.x, 0); // rotate 90° CCW = inward
              addEdgeBand(
                [new THREE.Vector3(W + offX, 0 + offY, 0), new THREE.Vector3(L + offX, L - W + offY, 0)],
                inward
              );
            }
            else if (side === 'right') addEdgeBand(
              [new THREE.Vector3(L + offX, L - W + offY, 0), new THREE.Vector3(L + offX, L + offY, 0)],
              new THREE.Vector3(-1, 0, 0)
            );
            else if (side === 'top') addEdgeBand(
              [new THREE.Vector3(L + offX, L + offY, 0), new THREE.Vector3(0 + offX, L + offY, 0)],
              new THREE.Vector3(0, -1, 0)
            );
            else if (side === 'left') addEdgeBand(
              [new THREE.Vector3(0 + offX, L + offY, 0), new THREE.Vector3(0 + offX, 0 + offY, 0)],
              new THREE.Vector3(1, 0, 0)
            );
            break;
        }
      });
    }

    // SideCuts Visualization (Quartz polished edges — same blue band as configurationEdges)
    // Uses same shape-specific logic (including arc bands) as configurationEdges rendering.
    if (plan.materialThickness?.canHaveSideCuts && plan.configurationSideCuts && plan.configurationSideCuts.length > 0) {
      plan.configurationSideCuts.forEach(sc => {
        const side = inferWorkbenchEdgeSide(plan as ConfigurationPlan, sc, 5) ?? this.inferEdgeSide(sc, L, W, plan.shape.shapeType);
        if (!side) return;
        switch (plan.shape.shapeType) {
          case ShapeTypeEnum.Rectangle: {
            const rectSegs = this.getRectSegmentsWithCutaways(L, W, plan.configurationCutaways);
            rectSegs.forEach(seg => {
              if (seg.side === side) {
                if (seg.isArc && seg.radius) {
                  addEdgeArcBand(seg.p1.x + offX, seg.p1.y + offY, seg.radius, seg.startAngle || 0, seg.endAngle || 0);
                } else {
                  const vec = new THREE.Vector3(seg.p2.x - seg.p1.x, seg.p2.y - seg.p1.y, 0).normalize();
                  addEdgeBand([
                    new THREE.Vector3(seg.p1.x + offX, seg.p1.y + offY, 0),
                    new THREE.Vector3(seg.p2.x + offX, seg.p2.y + offY, 0)
                  ], new THREE.Vector3(-vec.y, vec.x, 0));
                }
              }
            });
            break;
          }
          case ShapeTypeEnum.HalfMoon: {
            const rHMsc = W / 2;
            if (side === 'left') addEdgeBand([new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(0 + offX, W + offY, 0)], new THREE.Vector3(1, 0, 0));
            else if (side === 'top') addEdgeBand([new THREE.Vector3(0 + offX, W + offY, 0), new THREE.Vector3(L - rHMsc + offX, W + offY, 0)], new THREE.Vector3(0, -1, 0));
            else if (side === 'bottom') addEdgeBand([new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(L - rHMsc + offX, 0 + offY, 0)], new THREE.Vector3(0, 1, 0));
            else if (side === 'arc') addEdgeArcBand(L - rHMsc + offX, rHMsc + offY, rHMsc, -Math.PI / 2, Math.PI / 2);
            break;
          }
          case ShapeTypeEnum.Mushroom: {
            const rMsc = (plan.diameter || W) / 2;
            const isFlipMsc = plan.isFlip;
            const adjXMsc = Math.sqrt(Math.max(0, rMsc ** 2 - (W - rMsc) ** 2));
            const stopXMsc = L - rMsc - adjXMsc;
            if (side === 'left') addEdgeBand([new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(0 + offX, W + offY, 0)], new THREE.Vector3(1, 0, 0));
            else if (side === 'top') { const xEndT = isFlipMsc ? (L - rMsc) : stopXMsc; addEdgeBand([new THREE.Vector3(0 + offX, W + offY, 0), new THREE.Vector3(xEndT + offX, W + offY, 0)], new THREE.Vector3(0, -1, 0)); }
            else if (side === 'bottom') { const xEndB = isFlipMsc ? stopXMsc : (L - rMsc); addEdgeBand([new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(xEndB + offX, 0 + offY, 0)], new THREE.Vector3(0, 1, 0)); }
            else if (side === 'arc') { const start = isFlipMsc ? Math.PI / 2 : -Math.PI / 2; const arg = Math.max(-1, Math.min(1, (W - rMsc) / rMsc)); const thetaP = Math.asin(arg); const thetaEnd = isFlipMsc ? (-Math.PI - thetaP) : (Math.PI - thetaP); const cy = isFlipMsc ? (W - rMsc) : rMsc; addEdgeArcBand(L - rMsc + offX, cy + offY, rMsc, start, thetaEnd); }
            break;
          }
          case ShapeTypeEnum.Circle: {
            addEdgeArcBand(0, 0, (plan.diameter || 940) / 2, 0, Math.PI * 2);
            break;
          }
          case ShapeTypeEnum.Angle: {
            if (side === 'bottom') addEdgeBand([new THREE.Vector3(0 + offX, 0 + offY, 0), new THREE.Vector3(W + offX, 0 + offY, 0)], new THREE.Vector3(0, 1, 0));
            else if (side === 'diagonal') { const ddir = new THREE.Vector3(L - W, L - W, 0).normalize(); addEdgeBand([new THREE.Vector3(W + offX, 0 + offY, 0), new THREE.Vector3(L + offX, L - W + offY, 0)], new THREE.Vector3(-ddir.y, ddir.x, 0)); }
            else if (side === 'right') addEdgeBand([new THREE.Vector3(L + offX, L - W + offY, 0), new THREE.Vector3(L + offX, L + offY, 0)], new THREE.Vector3(-1, 0, 0));
            else if (side === 'top') addEdgeBand([new THREE.Vector3(L + offX, L + offY, 0), new THREE.Vector3(0 + offX, L + offY, 0)], new THREE.Vector3(0, -1, 0));
            else if (side === 'left') addEdgeBand([new THREE.Vector3(0 + offX, L + offY, 0), new THREE.Vector3(0 + offX, 0 + offY, 0)], new THREE.Vector3(1, 0, 0));
            break;
          }
        }
      });
    }

    // Jambs Visualization
    if (plan.configurationJambs && plan.configurationJambs.length > 0) {
      plan.configurationJambs.forEach((jamb: ConfigurationJamb) => {
        if (!jamb.startPoint || !jamb.endPoint) return;

        const pStart = new THREE.Vector3(jamb.startPoint.x, jamb.startPoint.y, 0).sub(center);
        const pEnd = new THREE.Vector3(jamb.endPoint.x, jamb.endPoint.y, 0).sub(center);

        const edgeVec = pEnd.clone().sub(pStart);
        const edgeLength = edgeVec.length();
        const height = jamb.height || 870;

        const jambGeom = new THREE.BoxGeometry(edgeLength, thickness, height);

        let mat: THREE.Material | THREE.Material[];
        if (texture) {
          const jambTex = texture.clone();
          jambTex.needsUpdate = true;
          jambTex.repeat.set(edgeLength / 500, height / 500);
          const texturedMat = new THREE.MeshBasicMaterial({ map: jambTex, color: 0xffffff });
          mat = [texturedMat, texturedMat, texturedMat, texturedMat, texturedMat, texturedMat];
        } else {
          mat = new THREE.MeshBasicMaterial({ color: 0xdddddd });
        }

        const mesh = new THREE.Mesh(jambGeom, mat);
        const angle = Math.atan2(edgeVec.y, edgeVec.x);
        mesh.rotation.z = angle;

        const mid = pStart.clone().add(pEnd).multiplyScalar(0.5);
        const toCenter = new THREE.Vector3(0, 0, 0).sub(mid).setZ(0).normalize();
        const offset = toCenter.multiplyScalar(thickness / 2);

        const posX = mid.x + offset.x;
        const posY = mid.y + offset.y;
        const z = -(thickness / 2) - (height / 2);

        mesh.position.set(posX, posY, z);

        const edges = new THREE.EdgesGeometry(jambGeom);
        const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x999999 }));

        const jambGroup = new THREE.Group();
        jambGroup.add(mesh);
        jambGroup.add(line);
        jambGroup.position.copy(mesh.position);
        jambGroup.rotation.copy(mesh.rotation);

        mesh.position.set(0, 0, 0);
        line.position.set(0, 0, 0);
        mesh.rotation.set(0, 0, 0);

        group.add(jambGroup);
        mesh.userData = { type: 'jamb', id: jamb.id };

        // 2D Indicator for Jamb (Orange Hatched Pattern outside the plan)
        const indicatorZ = thickness / 2 + 2;
        const indicatorWidth = 100; // Width for 2D visibility
        const indicatorGeom = new THREE.PlaneGeometry(edgeLength, indicatorWidth);
        
        // Procedural hatch shader — always crisp regardless of zoom
        const indicatorMat = new THREE.ShaderMaterial({
          transparent: true,
          side: THREE.DoubleSide,
          depthWrite: false,
          uniforms: {
            uColor: { value: new THREE.Color(0xE07000) },  // Dark orange
            uLineWidth: { value: 0.4 },    // Line thickness ratio (40% of spacing = thick bands)
            uSpacing: { value: 25.0 },      // Spacing in world units (mm) — large for bold hatching
            uSize: { value: new THREE.Vector2(edgeLength, indicatorWidth) }
          },
          vertexShader: `
            varying vec2 vUv;
            uniform vec2 uSize;
            varying vec2 vWorldPos;
            void main() {
              vUv = uv;
              vWorldPos = uv * uSize;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `,
          fragmentShader: `
            uniform vec3 uColor;
            uniform float uLineWidth;
            uniform float uSpacing;
            uniform vec2 uSize;
            varying vec2 vUv;
            varying vec2 vWorldPos;
            void main() {
              // Diagonal hatch: lines at 45 degrees
              float diag = vWorldPos.x + vWorldPos.y;
              float pattern = mod(diag, uSpacing);
              float line = step(pattern, uSpacing * uLineWidth);
              // Background fill + hatch lines
              float bgAlpha = 0.15;
              float lineAlpha = 0.85;
              float alpha = mix(bgAlpha, lineAlpha, line);
              gl_FragColor = vec4(uColor, alpha);
            }
          `
        });
        const indicatorMesh = new THREE.Mesh(indicatorGeom, indicatorMat);
        
        const indGroup = new THREE.Group();
        indGroup.add(indicatorMesh);
        
        // Compute outward normal from midpoint (away from center = opposite of toCenter direction)
        const outwardNorm = mid.clone().setZ(0).normalize();
        // Position: move from midpoint outward by half the indicator width
        // so the inner edge of the indicator is flush with the plan edge
        const outX = mid.x + outwardNorm.x * (indicatorWidth / 2);
        const outY = mid.y + outwardNorm.y * (indicatorWidth / 2);
        indGroup.position.set(outX, outY, indicatorZ);
        indGroup.rotation.z = angle;
        
        indicatorMesh.userData = { type: 'jamb-indicator', id: jamb.id };
        group.add(indGroup);

        // Render horizontal edge band (blue line) for the jamb
        // Not applicable for Quartz plans (polished edges) nor materials without edge bands (e.g. bois massif)
        if (jamb.horizontalWorkbenchConfigurationEdge && plan.material?.materialType !== MaterialTypeEnum.Quartz && plan.materialThickness?.canHaveEdges) {
          const jStart = new THREE.Vector3(jamb.startPoint.x + offX, jamb.startPoint.y + offY, 0);
          const jEnd = new THREE.Vector3(jamb.endPoint.x + offX, jamb.endPoint.y + offY, 0);
          const jDir = jEnd.clone().sub(jStart).normalize();
          // Normal pointing inward (toward plan center)
          const jNormal = new THREE.Vector3(-jDir.y, jDir.x, 0);
          // Check if normal points toward center, if not, flip it
          const jMid = jStart.clone().add(jEnd).multiplyScalar(0.5);
          if (jMid.clone().add(jNormal).length() > jMid.length()) {
            jNormal.negate();
          }
          addEdgeBand([jStart, jEnd], jNormal);
        }
      });
    }

    // Cutaways Visualization (2D Indicators)
    if (plan.configurationCutaways && plan.configurationCutaways.length > 0 &&
        plan.shape.shapeType !== ShapeTypeEnum.Circle) {
      const CHAMFER = 150;
      const FILLET = 100;

      const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
      const getCut = (x: number, y: number) => plan.configurationCutaways?.find((c: any) => isSamePoint(c.origin, { x, y }));

      // Define applicable right-angle corners per shape type
      // All coordinates are in uncentered shape space (origin at BL)
      let corners: { cut: any, cx: number, cy: number, dx: number, dy: number }[];

      if (plan.shape.shapeType === ShapeTypeEnum.Rectangle) {
        corners = [
          { cut: getCut(0, 0), cx: 0, cy: 0, dx: 1, dy: 1 },
          { cut: getCut(length, 0), cx: length, cy: 0, dx: -1, dy: 1 },
          { cut: getCut(length, width), cx: length, cy: width, dx: -1, dy: -1 },
          { cut: getCut(0, width), cx: 0, cy: width, dx: 1, dy: -1 }
        ];
      } else if (plan.shape.shapeType === ShapeTypeEnum.HalfMoon || plan.shape.shapeType === ShapeTypeEnum.Mushroom) {
        // Only left-side corners are right-angle (right side is circular)
        corners = [
          { cut: getCut(0, 0), cx: 0, cy: 0, dx: 1, dy: 1 },
          { cut: getCut(0, width), cx: 0, cy: width, dx: 1, dy: -1 }
        ];
      } else if (plan.shape.shapeType === ShapeTypeEnum.Angle) {
        // Origins stored with actual shape corner coords: TR=(length,length), TL=(0,length)
        const totalSize = length;
        corners = [
          { cut: getCut(0, 0), cx: 0, cy: 0, dx: 1, dy: 1 },
          { cut: getCut(length, length), cx: totalSize, cy: totalSize, dx: -1, dy: -1 },
          { cut: getCut(0, length), cx: 0, cy: totalSize, dx: 1, dy: -1 }
        ];
      } else {
        corners = [];
      }

      corners.forEach(corner => {
        if (!corner.cut) return;
        
        const S = (corner.cut.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
        const isChamfer = corner.cut.angleType === AngleTypeEnum.straight;
        
        const shape = new THREE.Shape();
        shape.moveTo(corner.cx, corner.cy);
        shape.lineTo(corner.cx + corner.dx * S, corner.cy);
        
        if (isChamfer) {
          shape.lineTo(corner.cx, corner.cy + corner.dy * S);
        } else {
          const centerX = corner.cx + corner.dx * S;
          const centerY = corner.cy + corner.dy * S;
          const a1 = Math.atan2(-corner.dy * S, 0);
          const a2 = Math.atan2(0, -corner.dx * S);
          shape.absarc(centerX, centerY, S, a1, a2, corner.dx * corner.dy > 0);
        }
        shape.lineTo(corner.cx, corner.cy);

        const indicatorGeom = new THREE.ShapeGeometry(shape);
        const indicatorMat = new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.4, side: THREE.DoubleSide, depthWrite: false });
        const indicatorMesh = new THREE.Mesh(indicatorGeom, indicatorMat);
        
        const indicatorEdges = new THREE.EdgesGeometry(indicatorGeom);
        const indicatorLine = new THREE.LineSegments(indicatorEdges, new THREE.LineDashedMaterial({ color: 0xff0000, dashSize: 10, gapSize: 5 }));
        indicatorLine.computeLineDistances();
        
        const indicatorZ = thickness / 2 + 2.5;
        indicatorMesh.position.set(-center.x, -center.y, indicatorZ);
        indicatorLine.position.set(-center.x, -center.y, indicatorZ);
        
        indicatorMesh.userData = { type: 'cutaway-indicator', id: corner.cut.id };
        indicatorLine.userData = { type: 'cutaway-indicator', id: corner.cut.id };
        
        group.add(indicatorMesh);
        group.add(indicatorLine);
      });
    }

    if ((plan as any).rotation) {
      group.rotation.z = (plan as any).rotation;
    } else if (plan.shapeRotationState !== undefined) {
      group.rotation.z = THREE.MathUtils.degToRad(plan.shapeRotationState * -90);
    }

    return group;
  }

  public createSplashbacks(plan: ConfigurationPlan, group: THREE.Group, thickness: number, texture: THREE.Texture | null, is3DMode: boolean = false) {
    if (!plan.configurationSplashbacks || plan.configurationSplashbacks.length === 0) return;

    const centerOffset = group.userData['centerOffset'] || { x: 0, y: 0 };
    const center = new THREE.Vector3(centerOffset.x, centerOffset.y, 0);

    plan.configurationSplashbacks.forEach((sb: any) => {
      if (!sb.startPoint || !sb.endPoint) return;

      const pStart = new THREE.Vector3(sb.startPoint.x, sb.startPoint.y, 0).sub(center);
      const pEnd = new THREE.Vector3(sb.endPoint.x, sb.endPoint.y, 0).sub(center);

      const edgeVec = pEnd.clone().sub(pStart);
      const edgeLength = edgeVec.length();
      const height = sb.height || 350;

      let mesh: THREE.Mesh;
      let line: THREE.Line;

      if (is3DMode) {
        let mat3D: THREE.Material | THREE.Material[];
        if (texture) {
          const sbTex = texture.clone();
          sbTex.needsUpdate = true;
          sbTex.rotation = Math.PI / 2;
          sbTex.repeat.set(edgeLength / 1000, height / 1000);
          const texturedMat = new THREE.MeshBasicMaterial({ map: sbTex, color: 0xffffff });
          const plainMat = new THREE.MeshBasicMaterial({ color: 0xdddddd });
          mat3D = [plainMat, plainMat, texturedMat, texturedMat, plainMat, plainMat];
        } else {
          mat3D = new THREE.MeshBasicMaterial({ color: 0xeeeeee });
        }
        const sbGeom3D = new THREE.BoxGeometry(edgeLength, thickness, height);
        mesh = new THREE.Mesh(sbGeom3D, mat3D);
        const mid = pStart.clone().add(pEnd).multiplyScalar(0.5);
        const z = (thickness / 2) + (height / 2);
        mesh.position.set(mid.x, mid.y, z);
        mesh.rotation.z = Math.atan2(edgeVec.y, edgeVec.x);
        const edges3D = new THREE.EdgesGeometry(sbGeom3D);
        line = new THREE.LineSegments(edges3D, new THREE.LineBasicMaterial({ color: 0x999999 }));
      } else {
        // Build a Shape with optional cutting holes so the mesh shows openings
        const sbShape = new THREE.Shape();
        const hw = edgeLength / 2;
        const hh = height / 2;
        sbShape.moveTo(-hw, -hh);
        sbShape.lineTo( hw, -hh);
        sbShape.lineTo( hw,  hh);
        sbShape.lineTo(-hw,  hh);
        sbShape.closePath();

        if (sb.configurationCuttings) {
          for (const cc of sb.configurationCuttings) {
            if (!cc.cutting) continue;
            const cw = cc.cutting.defaultWidth || 300;
            const ch = cc.cutting.defaultLength || 500;
            const ox = (cc.origin?.x ?? edgeLength / 2) - hw;
            const oy = (cc.origin?.y ?? height / 2) - hh;
            const hole = new THREE.Path();
            hole.moveTo(ox - cw / 2, oy - ch / 2);
            hole.lineTo(ox + cw / 2, oy - ch / 2);
            hole.lineTo(ox + cw / 2, oy + ch / 2);
            hole.lineTo(ox - cw / 2, oy + ch / 2);
            hole.closePath();
            sbShape.holes.push(hole);
          }
        }

        const sbGeom2D = new THREE.ShapeGeometry(sbShape);
        let mat2D: THREE.Material;
        if (texture) {
          const sbTex = texture.clone();
          sbTex.needsUpdate = true;
          sbTex.wrapS = THREE.RepeatWrapping;
          sbTex.wrapT = THREE.RepeatWrapping;
          // ShapeGeometry uses raw world coords as UVs (in mm), center is 0,0
          // repeat = 1/size maps [-size/2, size/2] to [-0.5, 0.5], offset 0.5 shifts to [0, 1]
          sbTex.repeat.set(1 / edgeLength, 1 / height);
          sbTex.offset.set(0.5, 0.5);
          mat2D = new THREE.MeshBasicMaterial({ map: sbTex, color: 0xffffff, side: THREE.DoubleSide });
        } else {
          mat2D = new THREE.MeshBasicMaterial({ color: 0xeeeeee, side: THREE.DoubleSide });
        }
        mesh = new THREE.Mesh(sbGeom2D, mat2D);

        const GAP = 200;
        const mid2D = pStart.clone().add(pEnd).multiplyScalar(0.5);
        let normal = new THREE.Vector3(-edgeVec.y, edgeVec.x, 0).normalize();
        if (normal.dot(mid2D) < 0) normal.negate();
        const pos = mid2D.clone().add(normal.multiplyScalar(GAP + hh));
        mesh.position.set(pos.x, pos.y, 10);
        mesh.rotation.z = Math.atan2(edgeVec.y, edgeVec.x);

        // Manual LineLoop outline (avoid EdgesGeometry showing triangulation)
        const outlinePoints = [
          new THREE.Vector3(-hw, -hh, 0),
          new THREE.Vector3( hw, -hh, 0),
          new THREE.Vector3( hw,  hh, 0),
          new THREE.Vector3(-hw,  hh, 0),
        ];
        const outlineGeo = new THREE.BufferGeometry().setFromPoints(outlinePoints);
        line = new THREE.LineLoop(outlineGeo, new THREE.LineBasicMaterial({ color: 0x999999 }));
      }

      const sbGroup = new THREE.Group();
      sbGroup.add(mesh);
      sbGroup.add(line);
      sbGroup.userData = {
        type: 'splashback',
        id: sb.id,
        height: height,
        edgeLength: edgeLength,
        splashback: sb,
        start: pStart,
        end: pEnd,
        displayOrder: sb.displayOrder,
        planOrder: plan.displayOrder
      };
      sbGroup.position.copy(mesh.position);
      sbGroup.rotation.copy(mesh.rotation);
      mesh.position.set(0, 0, 0);
      line.position.set(0, 0, 0);
      mesh.rotation.set(0, 0, 0);

      group.add(sbGroup);
    });
  }

  // --- Private Helpers ---

  private createEdgeStrips(plan: ConfigurationPlan, width: number, length: number, meshCenter: THREE.Vector3, texture?: THREE.Texture | null): THREE.Object3D[] {
    const strips: THREE.Object3D[] = [];
    if (!plan.configurationEdges || plan.configurationEdges.length === 0) return strips;

    const THICKNESS = 4;
    const HEIGHT = (plan.materialThickness && plan.materialThickness.thickness) ? plan.materialThickness.thickness : 38;

    const createMat = () => {
      if (texture) {
        const tex = texture.clone();
        tex.needsUpdate = true;
        tex.repeat.set(1, 1);
        return new THREE.MeshBasicMaterial({ map: tex, color: 0xffffff });
      }
      return new THREE.MeshBasicMaterial({ color: 0x88CC88 });
    };

    const createLinearStrip = (w: number, l: number, h: number, px: number, py: number, rz: number = 0) => {
      const geo = new THREE.BoxGeometry(w, l, h);
      const mesh = new THREE.Mesh(geo, createMat());
      mesh.position.set(px, py, 0);
      mesh.rotation.z = rz;
      return mesh;
    };

    const L = length;
    const W = width;
    const offX = -meshCenter.x;
    const offY = -meshCenter.y;

    plan.configurationEdges.forEach(edge => {
      const side = (edge as ConfigurationEdge3D).side;
      let mesh: THREE.Mesh | null = null;

      switch (plan.shape.shapeType) {
        case ShapeTypeEnum.Rectangle:
          const rectSegments = this.getRectSegmentsWithCutaways(L, W, plan.configurationCutaways);
          rectSegments.forEach(seg => {
            if (seg.side === side) {
              if (seg.isArc && seg.radius) {
                strips.push(this.createArcStrip(
                  seg.p1.x + offX, seg.p1.y + offY, seg.radius, THICKNESS,
                  seg.startAngle || 0, seg.endAngle || 0, HEIGHT, createMat()
                ));
              } else {
                const vec = new THREE.Vector2(seg.p2.x - seg.p1.x, seg.p2.y - seg.p1.y);
                const sLen = vec.length();
                const sAngle = Math.atan2(vec.y, vec.x);
                const mid = new THREE.Vector2((seg.p1.x + seg.p2.x) / 2, (seg.p1.y + seg.p2.y) / 2);

                // Normal vector for offset (outward)
                const normal = new THREE.Vector2(-vec.y, vec.x).normalize();
                const pos = mid.add(normal.multiplyScalar(THICKNESS / 2));

                strips.push(createLinearStrip(sLen, THICKNESS, HEIGHT, pos.x + offX, pos.y + offY, sAngle));
              }
            }
          });
          break;

        case ShapeTypeEnum.HalfMoon:
          const rHM = W / 2;
          if (side === 'left') mesh = createLinearStrip(THICKNESS, W, HEIGHT, 0 + offX - THICKNESS / 2, W / 2 + offY);
          else if (side === 'top') mesh = createLinearStrip(L - rHM, THICKNESS, HEIGHT, (L - rHM) / 2 + offX, W + offY + THICKNESS / 2);
          else if (side === 'bottom') mesh = createLinearStrip(L - rHM, THICKNESS, HEIGHT, (L - rHM) / 2 + offX, 0 + offY - THICKNESS / 2);
          else if (side === 'arc' || side === 'right') {
            mesh = this.createArcStrip(L - rHM + offX, rHM + offY, rHM, THICKNESS, -Math.PI / 2, Math.PI / 2, HEIGHT, createMat());
          }
          break;

        case ShapeTypeEnum.Mushroom:
          const rM = (plan.diameter || W) / 2;
          const isFlipM = plan.isFlip;
          const adjXM = Math.sqrt(Math.max(0, rM ** 2 - (W - rM) ** 2));
          const stopXM = L - rM - adjXM;

          if (side === 'left') mesh = createLinearStrip(THICKNESS, W, HEIGHT, 0 + offX - THICKNESS / 2, W / 2 + offY);
          else if (side === 'top') {
            const xStop = isFlipM ? (L - rM) : stopXM;
            mesh = createLinearStrip(xStop, THICKNESS, HEIGHT, xStop / 2 + offX, W + offY + THICKNESS / 2);
          }
          else if (side === 'bottom') {
            const xStop = isFlipM ? stopXM : (L - rM);
            mesh = createLinearStrip(xStop, THICKNESS, HEIGHT, xStop / 2 + offX, 0 + offY - THICKNESS / 2);
          }
          else if (side === 'arc' || side === 'right') {
            const cx = L - rM;
            const cy = isFlipM ? (W - rM) : rM;
            const start = isFlipM ? Math.PI / 2 : -Math.PI / 2;
            const arg = Math.max(-1, Math.min(1, (W - rM) / rM));
            const thetaPrincipal = Math.asin(arg);
            const thetaEnd = isFlipM ? (-Math.PI - thetaPrincipal) : (Math.PI - thetaPrincipal);
            mesh = this.createArcStrip(cx + offX, cy + offY, rM, THICKNESS, start, thetaEnd, HEIGHT, createMat());
          }
          break;

        case ShapeTypeEnum.Circle:
          const rC = (plan.diameter || 940) / 2;
          mesh = this.createArcStrip(0, 0, rC, THICKNESS, 0, Math.PI * 2, HEIGHT, createMat());
          break;

        case ShapeTypeEnum.Angle:
          if (side === 'bottom') mesh = createLinearStrip(W, THICKNESS, HEIGHT, W / 2 + offX, 0 + offY - THICKNESS / 2);
          else if (side === 'diagonal') {
            const diagLen = Math.sqrt(Math.pow(L - W, 2) + Math.pow(L - W, 2));
            const diagMidX = (W + L) / 2;
            const diagMidY = (L - W) / 2;
            mesh = createLinearStrip(diagLen, THICKNESS, HEIGHT, diagMidX + offX, diagMidY + offY, Math.PI / 4);
            const diagNormal = new THREE.Vector3(1, -1, 0).normalize();
            mesh.position.add(diagNormal.multiplyScalar(THICKNESS / 2));
          }
          else if (side === 'right') mesh = createLinearStrip(THICKNESS, W, HEIGHT, L + offX + THICKNESS / 2, (L - W + L) / 2 + offY);
          else if (side === 'top') mesh = createLinearStrip(L, THICKNESS, HEIGHT, L / 2 + offX, L + offY + THICKNESS / 2);
          else if (side === 'left') mesh = createLinearStrip(THICKNESS, L, HEIGHT, 0 + offX - THICKNESS / 2, L / 2 + offY);
          break;
      }

      if (mesh) {
        mesh.userData = { type: 'edgeStrip', side: side };
        strips.push(mesh);
      }
    });

    return strips;
  }

  private createArcStrip(cx: number, cy: number, radius: number, thickness: number, start: number, end: number, height: number, material: THREE.Material): THREE.Mesh {
    const segments = 64;
    const delta = end - start;

    const shape = new THREE.Shape();
    // Outer arc
    shape.moveTo(Math.cos(start) * (radius + thickness), Math.sin(start) * (radius + thickness));
    for (let i = 1; i <= segments; i++) {
      const theta = start + (i / segments) * delta;
      shape.lineTo(Math.cos(theta) * (radius + thickness), Math.sin(theta) * (radius + thickness));
    }
    // Inner arc
    for (let i = segments; i >= 0; i--) {
      const theta = start + (i / segments) * delta;
      shape.lineTo(Math.cos(theta) * radius, Math.sin(theta) * radius);
    }
    shape.closePath();

    const extrudeSettings = { depth: height, bevelEnabled: false };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(cx, cy, -height / 2);
    return mesh;
  }

  private createRectShape(width: number, length: number, cutaways?: ConfigurationCutaway[]): THREE.Shape {
    const shape = new THREE.Shape();
    const CHAMFER = 150;
    const FILLET = 100;

    const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
    const getCut = (x: number, y: number) => cutaways?.find(c => isSamePoint(c.origin, { x, y }));

    const c0 = getCut(0, 0);
    let startX = 0;
    if (c0) startX = (c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
    shape.moveTo(startX, 0);

    const c1 = getCut(width, 0);
    let endX = width;
    if (c1) endX -= (c1.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
    shape.lineTo(endX, 0);

    if (c1) {
      const size = (c1.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c1.angleType === AngleTypeEnum.straight) shape.lineTo(width, size);
      else shape.quadraticCurveTo(width, 0, width, size);
    }

    const c2 = getCut(width, length);
    let endY = length;
    if (c2) endY -= (c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
    shape.lineTo(width, endY);

    if (c2) {
      const size = (c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c2.angleType === AngleTypeEnum.straight) shape.lineTo(width - size, length);
      else shape.quadraticCurveTo(width, length, width - size, length);
    }

    const c3 = getCut(0, length);
    endX = 0;
    if (c3) endX += (c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
    shape.lineTo(endX, length);

    if (c3) {
      const size = (c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c3.angleType === AngleTypeEnum.straight) shape.lineTo(0, length - size);
      else shape.quadraticCurveTo(0, length, 0, length - size);
    }

    endY = 0;
    if (c0) endY += (c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
    shape.lineTo(0, endY);

    if (c0) {
      const size = (c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c0.angleType === AngleTypeEnum.straight) shape.lineTo(size, 0);
      else shape.quadraticCurveTo(0, 0, size, 0);
    }

    return shape;
  }

  private createHalfMoonShape(totalLength: number, depth: number, cutaways?: ConfigurationCutaway[]): THREE.Shape {
    const shape = new THREE.Shape();
    const radius = depth / 2;
    const CHAMFER = 150;
    const FILLET = 100;

    const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
    const getCut = (x: number, y: number) => cutaways?.find(c => isSamePoint(c.origin, { x, y }));

    const c0 = getCut(0, 0);     // BL corner
    const c3 = getCut(0, depth); // TL corner (stored as (0, plan.width))

    const size0 = c0 ? ((c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const size3 = c3 ? ((c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;

    shape.moveTo(size0, 0);
    shape.lineTo(totalLength - radius, 0);
    shape.absarc(totalLength - radius, radius, radius, -Math.PI / 2, Math.PI / 2, false);
    shape.lineTo(size3, depth);

    if (c3) {
      if (c3.angleType === AngleTypeEnum.straight) shape.lineTo(0, depth - size3);
      else shape.quadraticCurveTo(0, depth, 0, depth - size3);
    }

    shape.lineTo(0, size0);

    if (c0) {
      if (c0.angleType === AngleTypeEnum.straight) shape.lineTo(size0, 0);
      else shape.quadraticCurveTo(0, 0, size0, 0);
    }

    return shape;
  }

  private createMushroomShape(totalLength: number, rectWidth: number, diameter: number, isFlip: boolean, cutaways?: ConfigurationCutaway[]): THREE.Shape {
    const shape = new THREE.Shape();
    const radius = diameter / 2;
    const cx = totalLength - radius;
    const clamp = (val: number) => Math.max(-1, Math.min(1, val));
    const CHAMFER = 150;
    const FILLET = 100;

    // Calculate junction distance from circle center to rectangle boundary
    const adjX = Math.sqrt(Math.max(0, radius ** 2 - (rectWidth - radius) ** 2));

    const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
    const getCut = (x: number, y: number) => cutaways?.find(c => isSamePoint(c.origin, { x, y }));

    const c0 = getCut(0, 0);          // BL corner
    const c3 = getCut(0, rectWidth);  // TL corner (stored as (0, plan.width))

    const size0 = c0 ? ((c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const size3 = c3 ? ((c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;

    if (isFlip) {
      const cy = rectWidth - radius;
      // Start just below TL on left edge (after TL cutaway departure point)
      shape.moveTo(0, rectWidth - size3);
      shape.lineTo(0, size0);
      if (c0) {
        if (c0.angleType === AngleTypeEnum.straight) shape.lineTo(size0, 0);
        else shape.quadraticCurveTo(0, 0, size0, 0);
      }
      shape.lineTo(cx - adjX, 0);
      shape.absarc(cx, cy, radius, -Math.PI / 2, Math.PI / 2, false);
      shape.lineTo(size3, rectWidth);
      if (c3) {
        if (c3.angleType === AngleTypeEnum.straight) shape.lineTo(0, rectWidth - size3);
        else shape.quadraticCurveTo(0, rectWidth, 0, rectWidth - size3);
      }
    } else {
      const cy = radius;
      shape.moveTo(size0, 0);
      shape.lineTo(cx, 0);
      const arg = clamp((rectWidth - radius) / radius);
      const thetaEnd = Math.PI - Math.asin(arg);
      shape.absarc(cx, cy, radius, -Math.PI / 2, thetaEnd, false);
      shape.lineTo(size3, rectWidth);
      if (c3) {
        if (c3.angleType === AngleTypeEnum.straight) shape.lineTo(0, rectWidth - size3);
        else shape.quadraticCurveTo(0, rectWidth, 0, rectWidth - size3);
      }
      shape.lineTo(0, size0);
      if (c0) {
        if (c0.angleType === AngleTypeEnum.straight) shape.lineTo(size0, 0);
        else shape.quadraticCurveTo(0, 0, size0, 0);
      }
    }
    return shape;
  }

  private createAngleShape(width: number, length: number, cutaways?: ConfigurationCutaway[]): THREE.Shape {
    const totalSize = Number(length);
    let straightSize = Number(width);
    if (straightSize > totalSize) straightSize = totalSize;

    const shape = new THREE.Shape();
    const CHAMFER = 150;
    const FILLET = 100;

    const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
    const getCut = (x: number, y: number) => cutaways?.find(c => isSamePoint(c.origin, { x, y }));

    // Origins use actual shape corner coords: BL=(0,0), TR=(length,length), TL=(0,length)
    const c0 = getCut(0, 0);           // BL corner → shape (0, 0)
    const c2 = getCut(length, length); // TR corner → shape (totalSize, totalSize)
    const c3 = getCut(0, length);      // TL corner → shape (0, totalSize)

    const size0 = c0 ? ((c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const size2 = c2 ? ((c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const size3 = c3 ? ((c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;

    shape.moveTo(size0, 0);
    shape.lineTo(straightSize, 0);
    shape.lineTo(totalSize, totalSize - straightSize);
    shape.lineTo(totalSize, totalSize - size2);

    if (c2) {
      if (c2.angleType === AngleTypeEnum.straight) shape.lineTo(totalSize - size2, totalSize);
      else shape.quadraticCurveTo(totalSize, totalSize, totalSize - size2, totalSize);
    }

    shape.lineTo(size3, totalSize);

    if (c3) {
      if (c3.angleType === AngleTypeEnum.straight) shape.lineTo(0, totalSize - size3);
      else shape.quadraticCurveTo(0, totalSize, 0, totalSize - size3);
    }

    shape.lineTo(0, size0);

    if (c0) {
      if (c0.angleType === AngleTypeEnum.straight) shape.lineTo(size0, 0);
      else shape.quadraticCurveTo(0, 0, size0, 0);
    }

    return shape;
  }

  private createCircleShape(diameter: number): THREE.Shape {
    const shape = new THREE.Shape();
    const radius = diameter / 2;
    shape.moveTo(radius * 2, radius);
    shape.absarc(radius, radius, radius, 0, Math.PI * 2, false);
    return shape;
  }

  private addCuttingHoles(shape: THREE.Shape, plan: { length: number; width: number; configurationCuttings?: any[] }): void {
    if (!plan.configurationCuttings?.length) return;
    for (const cc of plan.configurationCuttings) {
      if (!cc.cutting) continue;
      let cw = cc.cutting.defaultWidth || 300;
      let ch = cc.cutting.defaultLength || 500;
      // Swap dimensions for 90° / 270° rotation
      if ((cc.rotationState || 0) % 2 === 1) { [cw, ch] = [ch, cw]; }
      const ox = cc.origin?.x ?? plan.length / 2;
      const oy = cc.origin?.y ?? plan.width / 2;
      const hole = new THREE.Path();
      hole.moveTo(ox - cw / 2, oy - ch / 2);
      hole.lineTo(ox + cw / 2, oy - ch / 2);
      hole.lineTo(ox + cw / 2, oy + ch / 2);
      hole.lineTo(ox - cw / 2, oy + ch / 2);
      hole.closePath();
      shape.holes.push(hole);
    }
  }

  private getRectSegmentsWithCutaways(width: number, length: number, cutaways?: ConfigurationCutaway[]): any[] {
    const segments: any[] = [];
    const CHAMFER = 150;
    const FILLET = 100;

    const isSamePoint = (p1: any, p2: any) => Math.abs(p1.x - p2.x) < 1 && Math.abs(p1.y - p2.y) < 1;
    const getCut = (x: number, y: number) => cutaways?.find(c => isSamePoint(c.origin, { x, y }));

    const c0 = getCut(0, 0); // BL
    const c1 = getCut(width, 0); // BR
    const c2 = getCut(width, length); // TR
    const c3 = getCut(0, length); // TL

    // Helper to add segment
    const add = (p1: any, p2: any, side: string, isArc: boolean = false, radius?: number, start?: number, end?: number) => {
      segments.push({ p1, p2, side, isArc, radius, startAngle: start, endAngle: end });
    };

    // Bottom Side
    const bStart = c0 ? ((c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const bEnd = c1 ? width - ((c1.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : width;
    add({ x: bStart, y: 0 }, { x: bEnd, y: 0 }, 'bottom');

    // BR Cutaway
    if (c1) {
      const size = (c1.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c1.angleType === AngleTypeEnum.straight) add({ x: width - size, y: 0 }, { x: width, y: size }, 'bottom'); // Part of bottom/right transition
      else add({ x: width - size, y: size }, null, 'bottom', true, size, -Math.PI / 2, 0);
    }

    // Right Side
    const rStart = c1 ? ((c1.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    const rEnd = c2 ? length - ((c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : length;
    add({ x: width, y: rStart }, { x: width, y: rEnd }, 'right');

    // TR Cutaway
    if (c2) {
      const size = (c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c2.angleType === AngleTypeEnum.straight) add({ x: width, y: length - size }, { x: width - size, y: length }, 'right');
      else add({ x: width - size, y: length - size }, null, 'right', true, size, 0, Math.PI / 2);
    }

    // Top Side
    const tStart = c2 ? width - ((c2.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : width;
    const tEnd = c3 ? ((c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    add({ x: tStart, y: length }, { x: tEnd, y: length }, 'top');

    // TL Cutaway
    if (c3) {
      const size = (c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c3.angleType === AngleTypeEnum.straight) add({ x: size, y: length }, { x: 0, y: length - size }, 'top');
      else add({ x: size, y: length - size }, null, 'top', true, size, Math.PI / 2, Math.PI);
    }

    // Left Side
    const lStart = c3 ? length - ((c3.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : length;
    const lEnd = c0 ? ((c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET) : 0;
    add({ x: 0, y: lStart }, { x: 0, y: lEnd }, 'left');

    // BL Cutaway
    if (c0) {
      const size = (c0.angleType === AngleTypeEnum.straight) ? CHAMFER : FILLET;
      if (c0.angleType === AngleTypeEnum.straight) add({ x: 0, y: size }, { x: size, y: 0 }, 'left');
      else add({ x: size, y: size }, null, 'left', true, size, Math.PI, 1.5 * Math.PI);
    }

    return segments;
  }

  /**
   * Reconstructs the 'side' identifier from a ConfigurationEdge's startPoint/endPoint.
   * Used when the transient 'side' property is missing (e.g. after project reload).
   */
  private inferEdgeSide(edge: any, L: number, W: number, shapeType: ShapeTypeEnum): string | null {
    const sp = edge.startPoint;
    const ep = edge.endPoint;
    if (!sp || !ep) return null;

    const eps = 5; // mm tolerance

    switch (shapeType) {
      case ShapeTypeEnum.Rectangle:
      case ShapeTypeEnum.HalfMoon:
      case ShapeTypeEnum.Mushroom:
        if (Math.abs(sp.y) < eps && Math.abs(ep.y) < eps) return 'bottom';
        if (Math.abs(sp.y - W) < eps && Math.abs(ep.y - W) < eps) return 'top';
        if (Math.abs(sp.x) < eps && Math.abs(ep.x) < eps) return 'left';
        if (Math.abs(sp.x - L) < eps && Math.abs(ep.x - L) < eps) return 'right';
        // Arc side: both points are (0,0) since segStart/segEnd not set in toggleEdgeSelection
        if (Math.abs(sp.x) < eps && Math.abs(sp.y) < eps && Math.abs(ep.x) < eps && Math.abs(ep.y) < eps) return 'arc';
        return null;
      case ShapeTypeEnum.Circle:
        return 'circle';
      case ShapeTypeEnum.Angle:
        if (Math.abs(sp.y) < eps && Math.abs(ep.y) < eps) return 'bottom';
        if (Math.abs(sp.y - L) < eps && Math.abs(ep.y - L) < eps) return 'top';
        if (Math.abs(sp.x) < eps && Math.abs(ep.x) < eps) return 'left';
        if (Math.abs(sp.x - L) < eps && Math.abs(ep.x - L) < eps) return 'right';
        // Diagonal: one point at (W,0) and one at (L, L-W)
        if (Math.abs(sp.x - W) < eps && Math.abs(sp.y) < eps) return 'diagonal';
        if (Math.abs(ep.x - W) < eps && Math.abs(ep.y) < eps) return 'diagonal';
        return null;
      default:
        return null;
    }
  }
}
